import { Suspense } from 'react';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Page, PageBody } from '@customerio/pluma-components/react';
import { ObjectsLandingPage, useObjectPlanDetails } from '@/features/objects';
import { LoadingPage } from '@/components/page/loading';
import { DocumentTitle } from '@/components/page/document-title';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useObjectTypesPolling } from '@/services/db/object-type/queries';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';

function ObjectsIndexContent() {
  const workspaceId = requireWorkspaceId();
  const router = useRouter();
  const { hasAnyPermission } = usePermissions();
  const planDetails = useObjectPlanDetails();
  const { data: objectTypes } = useObjectTypesPolling({ queryConfig: { staleTime: 0 } });
  const rootHref = router.buildLocation({ to: '/workspaces/$workspaceId/objects', params: { workspaceId } }).href;

  useRegisterBreadcrumbs([{ id: 'objects', text: 'Custom objects', href: rootHref }]);

  const firstType = objectTypes[0];
  if (firstType != null) {
    const href = router.buildLocation({
      to: '/workspaces/$workspaceId/objects/$slug',
      params: { workspaceId, slug: firstType.slug },
    }).href;
    return <SafeNavigate to={href} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  const createHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/objects/new',
    params: { workspaceId },
  }).href;

  return (
    <Page>
      <PageBody>
        <ObjectsLandingPage
          canConfigure={hasAnyPermission(
            'journeys/object_types/create',
            'journeys/object_types/edit',
            'journeys/object_types/delete',
          )}
          createHref={createHref}
          planDetails={planDetails}
        />
      </PageBody>
    </Page>
  );
}

export default function ObjectsIndexPage() {
  return (
    <>
      <DocumentTitle title="Custom objects" />
      <Suspense fallback={<LoadingPage />}>
        <ObjectsIndexContent />
      </Suspense>
    </>
  );
}
